import { createStyles, Grid, useMantineTheme } from '@mantine/core';
import {
  IconCalendarEvent,
  IconUserCircle,
  IconShield,
  IconInfoSquare,
  IconCalendarDue,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Fragment, useMemo, useState } from 'react';
import type {
  ContestSettings,
  SlateLabelEnum,
  Sport,
} from '@betterpool/api-types/contests-service';
import { ContestType } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { format } from 'date-fns';
import { useRouter } from 'next/router';

import { Match } from '~/domains/common/components/Match/Match';
import dt from '~/testing';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { pluralizeSlateLabel } from '~/domains/contest/domains/managev2/domains/common/utils/pluralizeSlateLabel';
import { LinkButton } from '~/domains/design-system/Button';
import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { Text } from '~/domains/design-system/Text';

import {
  Section,
  SectionContent,
  SectionGrid,
  SectionGridCol,
  SectionGridLabel,
  SectionGridValue,
  SectionTitle,
} from '../components';
import ScheduleModal from '../Schedule/ScheduleModal';
import useSegmentedTabs from '../Schedule/useSegmentedTabs';

import ContestIcon from './ContestIcon';
import { getSportText } from './about.utils';
import GameModal from './Game/GameModal';
import getPickDeadlineValue, { isKnockoutStagePickDeadline } from './getPickDeadlineValue';

const useStyles = createStyles((theme) => ({
  infoIcon: {
    cursor: 'pointer',
    marginLeft: theme.other.spacing._4,
  },
  labelWithTooltip: {
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

type AboutProps = {
  owner: string;
  rosterRequirements: string;
  ownerHref: string;
  contestType: ContestType;
  description?: string;
  slatesInfo?: string;
  createdByAdmin?: boolean;
  maxEntriesPerUser?: number;
  isSidePanel?: boolean;
  sport?: Sport;
  leagueName?: string;
  pickDeadlineType?: FullContest['settings']['pickDeadlineType'];
  entryDeadline: Date;
  scheduleId?: string;
  finalSlateId?: string;
  slateLabel?: SlateLabelEnum;
  contestSlates?: ContestSlate[];
  /** Temporary until we get a better fix in */
  newContestSlates?: ContestSlate[];
  segments?: ContestSettings['segments'];
  isEntryDeadlinePending?: boolean;
  groupStageSlateId?: string;
};

function About({
  description,
  rosterRequirements,
  owner,
  ownerHref,
  contestType,
  slatesInfo,
  createdByAdmin,
  maxEntriesPerUser,
  isSidePanel,
  sport,
  leagueName,
  pickDeadlineType,
  entryDeadline,
  scheduleId,
  finalSlateId,
  slateLabel,
  contestSlates,
  newContestSlates,
  segments,
  isEntryDeadlinePending,
  groupStageSlateId,
}: AboutProps) {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const { t } = useTranslation('contest');

  const isMultiEntry = maxEntriesPerUser > 1;

  const pickDeadlineValue = isEntryDeadlinePending
    ? t('detail.firstTeeTimeTbd')
    : getPickDeadlineValue({
        contestType,
        pickDeadlineType,
        slateLabel,
        slates: newContestSlates,
      });
  const pickDeadlineLabel = isKnockoutStagePickDeadline({
    contestType,
    groupStageSlateId,
  })
    ? t('detail.about.info.pickDeadlineKnockoutStage')
    : t('detail.about.info.pickDeadline');

  const { activeTab, tabs, setActiveTab } = useSegmentedTabs({
    slateLabel,
    slates: contestSlates,
    overallLength: segments?.data?.length ?? contestSlates?.length,
  });

  const showGameModal = sport !== 'golf';

  const [firstSlate] = contestSlates ?? [];

  const values = useMemo(
    () =>
      [
        {
          'data-test-id': dt.contestDetail.section.about.sport,
          Icon: () => <ContestIcon className="icon" sport={sport} />,
          id: 1,
          label: t('detail.about.info.sport'),
          value: leagueName ?? getSportText(sport),
        },
        {
          'data-test-id': dt.contestDetail.section.about.contestType,
          Icon: () => <IconShield className="icon" stroke={2} size={20} />,
          id: 2,
          label: t('detail.about.info.contestType'),
          value: (
            <Match value={contestType}>
              <Match.On value={[ContestType.PLAYER_TIER]}>{t('common:contest.tiers')}</Match.On>
              <Match.On value={[ContestType.TEAM_PICK_EM]}>{t('common:contest.pickEm')}</Match.On>
              <Match.On value={[ContestType.TEAM_SURVIVOR]}>
                {t('common:contest.survivor')}
              </Match.On>
              <Match.On value={[ContestType.PLAYER_ONE_AND_DONE]}>
                {t('common:contest.oneAndDone')}
              </Match.On>
              <Match.On value={[ContestType.PLAYER_SURVIVOR]}>
                {t('common:contest.playerSurvivor')}
              </Match.On>
              <Match.On value={[ContestType.SQUARES]}>{t('common:contest.squares')}</Match.On>
              <Match.On value={[ContestType.TARGET_SCORE_BINGO]}>
                {t('common:contest.targetScore')}
              </Match.On>
              <Match.On value={[ContestType.WINS_POOL]}>{t('common:contest.wins')}</Match.On>
              <Match.On value={[ContestType.SHARES]}>{t('common:contest.shares')}</Match.On>
              <Match.On value={[ContestType.TEAM_BRACKETS]}>
                {t('common:contest.brackets')}
              </Match.On>
            </Match>
          ),
        },
        {
          'data-test-id': dt.contestDetail.section.about.multiEntry,
          Icon: () => <IconInfoSquare className="icon" stroke={2} size={20} />,
          id: 3,
          label: t('detail.about.info.multiEntry'),
          value: isMultiEntry
            ? t('detail.about.info.yes', { entries: maxEntriesPerUser })
            : t(`detail.about.info.no`),
        },
        {
          'data-test-id': dt.contestDetail.section.about.creator,
          Icon: () => <IconUserCircle className="icon" stroke={2} size={20} />,
          id: 4,
          label: t('detail.about.info.creator'),
          value: owner ? (
            <LinkButton variant="info-fill" size="medium" onClick={() => router.push(ownerHref)}>
              {createdByAdmin ? t('detail.about.info.adminCreator') : owner}
            </LinkButton>
          ) : (
            t('detail.about.info.adminCreator')
          ),
        },
        {
          'data-test-id': dt.contestDetail.section.about.slate,
          Icon: () => <IconCalendarEvent className="icon" stroke={2} size={20} />,
          id: 5,
          label: t('detail.about.info.slate'),
          value: (
            <LinkButton variant="info-fill" size="medium" onClick={() => setIsModalOpen(true)}>
              {t('detail.about.game.action')} {pluralizeSlateLabel(2, slateLabel)?.toLowerCase()}
            </LinkButton>
          ),
        },
        {
          'data-test-id': dt.contestDetail.section.about.entryDeadline,
          Icon: () => <IconCalendarDue className="icon" stroke={2} size={20} />,
          id: 6,
          label: (
            <div className={classes.labelWithTooltip}>
              {t('detail.about.info.entryDeadline')}
              {firstSlate && (
                <Tooltip
                  withinPortal
                  withArrow
                  multiline
                  labels={[
                    {
                      label: (
                        <Text variant="body-medium">
                          {t('detail.about.info.entryDeadlineTooltip', {
                            slateName: firstSlate.name,
                          })}
                        </Text>
                      ),
                    },
                  ]}
                >
                  <IconInfoCircle className={classes.infoIcon} size={16} />
                </Tooltip>
              )}
            </div>
          ),
          value: isEntryDeadlinePending
            ? t('detail.firstTeeTimeTbd')
            : format(entryDeadline, "M/d '@' h:mm a"),
        },
        pickDeadlineValue && {
          'data-test-id': dt.contestDetail.section.about.pickDeadline,
          Icon: () => <IconCalendarDue className="icon" stroke={2} size={20} />,
          id: 7,
          label: (
            <div className={classes.labelWithTooltip}>
              {pickDeadlineLabel}
              <Tooltip
                withinPortal
                withArrow
                multiline
                labels={[
                  {
                    label: (
                      <Text variant="body-medium">
                        {t('detail.about.info.pickDeadlineTooltip')}
                      </Text>
                    ),
                  },
                ]}
              >
                <IconInfoCircle className={classes.infoIcon} size={16} />
              </Tooltip>
            </div>
          ),
          value: pickDeadlineValue,
        },
      ].filter(Boolean),
    [
      t,
      sport,
      leagueName,
      contestType,
      isMultiEntry,
      maxEntriesPerUser,
      owner,
      createdByAdmin,
      slateLabel,
      firstSlate,
      classes.infoIcon,
      classes.labelWithTooltip,
      entryDeadline,
      isEntryDeadlinePending,
      pickDeadlineLabel,
      pickDeadlineValue,
      router,
      ownerHref,
    ]
  );

  return (
    <Section>
      <SectionTitle data-test-id={dt.contestDetail.section.about.title} isSidePanel={isSidePanel}>
        {t('detail.about.title')}
      </SectionTitle>
      <SectionContent isSidePanel={isSidePanel}>
        {description ? (
          <SectionGridLabel
            data-test-id={dt.contestDetail.section.about.description}
            isSidePanel={isSidePanel}
            mb={theme.spacing.lg}
            dangerouslySetInnerHTML={{ __html: toSafeMarkup(description) }}
          />
        ) : null}
        {rosterRequirements ? (
          <SectionGridLabel
            data-test-id={dt.contestDetail.section.about.rosterRequirements}
            isSidePanel={isSidePanel}
            mb={theme.spacing.lg}
          >
            {rosterRequirements}
          </SectionGridLabel>
        ) : null}
        {values.map(({ label, Icon, value, id, ...rest }) => (
          <Fragment key={id}>
            <SectionGrid data-test-id={rest['data-test-id']}>
              <SectionGridCol span={6} xs={5} md={6}>
                <Grid align="center">
                  <Icon />
                  <SectionGridLabel isSidePanel={isSidePanel} ml="md">
                    {label}
                  </SectionGridLabel>
                </Grid>
              </SectionGridCol>
              <SectionGridCol span={6} xs={7} md={6}>
                <SectionGridValue isSidePanel={isSidePanel}>{value}</SectionGridValue>
              </SectionGridCol>
            </SectionGrid>
          </Fragment>
        ))}
        {showGameModal ? (
          <GameModal
            scheduleId={scheduleId}
            contestType={contestType}
            finalSlateId={finalSlateId}
            slatesInfo={slatesInfo}
            groupStageSlateId={groupStageSlateId}
            isOpen={isModalOpen}
            slateLabel={slateLabel}
            onClose={() => setIsModalOpen(false)}
          />
        ) : (
          <ScheduleModal
            isSidePanel={isSidePanel}
            isOpen={isModalOpen}
            slates={contestSlates}
            segments={segments}
            onClose={() => setIsModalOpen(false)}
            slateLabel={slateLabel}
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )}
      </SectionContent>
    </Section>
  );
}

export default About;

export type { AboutProps };
